import React from 'react';
import Select from 'react-select';
import { QRCODE_SCALE } from '../../constants/config';
import { TargetNode } from '../../models/TargetNode';

interface ScaleOption {
  value: number;
  scale: number;
  label: string;
}

interface QRcodeSelectorProps {
  node: TargetNode;
  className?: string;
  onChangeQRcode: (option: ScaleOption | null) => void;
}

const QRcodeSelector = ({ node, className, onChangeQRcode }: QRcodeSelectorProps) => {
  // Root targets have no QR scale to set.
  if (!node.parent) {
    return null;
  }

  // Read the scale straight from the persisted c74. There used to be a branch
  // preferring a `node.qrcodeid` scratch field and reading `.value` off it — but
  // the editor stores a plain NUMBER there, so `.value` was always undefined and
  // the label fell through to the QRCODE_SCALE[2] fallback. Every scale therefore
  // displayed as "L" once the selector had been touched (except XXL, whose id is
  // 0 and so failed the truthy check and took the correct branch by accident).
  // c74 is written synchronously by updateCExtras on every change, so the scratch
  // field carried nothing this doesn't. Default 2 ('L') matches the editor's seed.
  const storedId = node.extra?.c?.c74;
  const qrcodeId = typeof storedId === 'undefined' ? 2 : storedId;

  // react-select wants the option object, not the bare id. Passing the id meant
  // nothing ever matched, which is why `placeholder` was doing `value`'s job.
  const selected = QRCODE_SCALE.find((option: ScaleOption) => option.value === qrcodeId)
    || QRCODE_SCALE[2];

  return (
    <div className={className}>
      <Select
        className={'qrcode-selector'}
        classNamePrefix={'react-select'}
        value={selected}
        options={QRCODE_SCALE}
        onChange={onChangeQRcode} />
    </div>
  );
};

export default React.memo(QRcodeSelector);
